import classNames from "classnames"
import React, { useCallback, useState } from "react"

import { MAIN_LINK_CLASSES } from "."
import { MainLink } from "./NavHeading"
import SubMenuBlock from "./SubMenuBlock"
import { Color } from "../../../constants/V2/color"
import { handleOnClick } from "../../../utils/buttonEventHandler"

import Link from "@components/elements/Link"
import ChevronIcon from "@components/elements/V2/Icons/ChevronIcon"
import Typography from "@components/elements/V2/Typography"

interface MobileNavigationPanelsProps {
  mainLinks: MainLink[]
  mode: "light" | "dark"
  scrolled: boolean
  secondaryButtonText: string
  secondaryButtonLink: string
  secondaryButtonTrackingEvent?: string
  secondaryButtonTrackingEventKey?: string
  secondaryButtonTrackingEventValue?: string
  handleMainLinkClick?: (menuId: number) => void
  handleMainLinkClose?: () => void
}

const MobileNavPanels: React.FC<MobileNavigationPanelsProps> = ({
  mainLinks,
  mode,
  scrolled,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonTrackingEvent,
  secondaryButtonTrackingEventKey,
  secondaryButtonTrackingEventValue,
  handleMainLinkClick,
  handleMainLinkClose,
}) => {
  const [activeSubMenu, setActiveSubMenu] = useState<number | undefined>()
  const [activeSubSection, setActiveSubSection] = useState<number | undefined>()

  const handleMainLinkClickCallback = useCallback(
    (menuId: number) => {
      if (typeof menuId === "number") {
        handleMainLinkClick?.(menuId)
        // Tapping a main‐link on mobile: open/close Panel #2
        setActiveSubMenu((prev) => (prev === menuId ? undefined : menuId))
        setActiveSubSection(undefined) // reset deeper section
      }
    },
    [handleMainLinkClick]
  )

  const handleBackToMainMenu = useCallback(() => {
    setActiveSubMenu(undefined)
    handleMainLinkClose?.()
    setActiveSubSection(undefined)
  }, [handleMainLinkClose])

  const handleBackToSubMenu = useCallback(() => {
    setActiveSubSection(undefined)
  }, [])

  const handleSectionClick = useCallback((sectionIndex: number) => {
    setActiveSubSection(sectionIndex)
  }, [])

  const PANEL_BASE_CLASSES =
    "absolute pt-gutter-xs px-gutter-xs sm:px-gutter-sm md:px-gutter-md left-0 top-0 h-full w-full bg-white transition-transform duration-300 ease-in-out"

  return (
    <div className="relative h-full w-full">
      {/* ─ Panel #1: Main Links ── */}
      <div
        className={classNames(PANEL_BASE_CLASSES, {
          "translate-x-0": activeSubMenu === undefined,
          "-translate-x-full": activeSubMenu !== undefined,
        })}
      >
        <div className="h-fit overflow-y-auto">
          {mainLinks.map((mainLink, index) =>
            mainLink.link ? (
              <Link
                key={`mobile-section-${index}`}
                link={mainLink.link}
                className="hover:bg-gray-50 mb-24 flex w-full items-center justify-between sm:max-w-[330px]"
              >
                <Typography
                  color={Color.Charcoal}
                  font="grotesk"
                  weight="medium"
                  size="body-lg-fixed"
                  text={mainLink.label}
                />
                <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
              </Link>
            ) : (
              <button
                key={`mobile-section-${index}`}
                data-testid={`mainlink-${mainLink.label}`}
                onClick={() => handleMainLinkClickCallback?.(index)}
                className="hover:bg-gray-50 mb-24 flex w-full items-center justify-between sm:max-w-[330px]"
              >
                <Typography
                  color={Color.Charcoal}
                  font="grotesk"
                  weight="medium"
                  size="body-lg-fixed"
                  text={mainLink.label}
                />
                <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
              </button>
            )
          )}
        </div>
        {/* ─ Mobile + Tablet Bottom Buttons ── */}
        <div className="flex flex-col border-t border-charcoal/10 sm:border-none lg:hidden">
          <Link
            link="https://support.yoco.help/en/"
            className={classNames(
              "!pb-0 transition-opacity duration-200 ease-in-out",
              MAIN_LINK_CLASSES,
              {
                "text-black lg:text-white": mode === "dark" && !scrolled,
              },
              { "text-black": mode === "light" && scrolled }
            )}
          >
            <Typography
              text="Support"
              size="body-md-fixed"
              font="grotesk"
              weight="medium"
              color={Color.Charcoal}
            />
          </Link>
          <Link
            className={classNames(
              "!pb-0 transition-opacity duration-200 ease-in-out",
              MAIN_LINK_CLASSES,
              {
                "text-black lg:text-white": mode === "dark" && !scrolled,
              },
              { "text-black": mode === "light" && scrolled }
            )}
            link={secondaryButtonLink}
            onClick={(e) =>
              handleOnClick({
                e,
                trackingEvent: secondaryButtonTrackingEvent,
                trackingEventKey: secondaryButtonTrackingEventKey,
                trackingEventValue: secondaryButtonTrackingEventValue,
              })
            }
          >
            <Typography
              text={secondaryButtonText}
              size="body-md-fixed"
              font="grotesk"
              weight="medium"
              color={Color.Charcoal}
            />
          </Link>
        </div>
      </div>

      {/* ─ Panel #2: Submenu for mainLink ── */}
      <div
        className={classNames(PANEL_BASE_CLASSES, "flex sm:flex-col", {
          "translate-x-0":
            activeSubMenu !== undefined && activeSubSection === undefined,
          "translate-x-full": activeSubMenu === undefined,
          "-translate-x-full": activeSubSection !== undefined,
        })}
      >
        {/* Back Button (to Panel #1) */}
        <button className="flex gap-8" onClick={handleBackToMainMenu}>
          <ChevronIcon className="rotate-90" />
          <Typography
            color={Color.Charcoal}
            font="grotesk"
            weight="medium"
            size="body-md-fixed"
            text="Back"
            className="mb-32 hidden pt-[2px] sm:block"
          />
        </button>
        <div className="w-full pl-16 sm:pl-32">
          {activeSubMenu !== undefined && (
            <div className="w-full flex-1 overflow-y-auto">
              {mainLinks[activeSubMenu].menuColumnBlocks && (
                <div className="space-y-6">
                  {mainLinks[activeSubMenu].menuColumnBlocks!.map(
                    (menuBlock, bIdx) => (
                      <SubMenuBlock
                        key={`navigation-submenu-${bIdx}`}
                        {...menuBlock}
                      />
                    )
                  )}
                </div>
              )}

              {/* ─ If this mainLink has subMenu.content, show section labels ─── */}
              {mainLinks[activeSubMenu]?.subMenu && (
                <div className="w-full">
                  <Typography
                    color={Color.Charcoal}
                    font="grotesk"
                    weight="book"
                    size="subscript-sm-fixed"
                    className="mb-20 pt-[6px] uppercase"
                    text={mainLinks[activeSubMenu].label}
                  />
                  {mainLinks[activeSubMenu].subMenu?.content.map(
                    (section, sIdx) => (
                      <button
                        key={`mobile-section-${sIdx}`}
                        onClick={() => handleSectionClick(sIdx)}
                        className="hover:bg-gray-50 mb-24 flex w-full items-center justify-between sm:max-w-[330px]"
                      >
                        <Typography
                          color={Color.Charcoal}
                          font="grotesk"
                          weight="medium"
                          size="body-lg-fixed"
                          text={section.label}
                        />
                        <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─ Panel #3: Third‐level submenu (section's menuContent) ─────────── */}
      <div
        className={classNames(PANEL_BASE_CLASSES, "flex sm:flex-col", {
          "translate-x-0": activeSubSection !== undefined,
          "translate-x-full": activeSubSection === undefined,
        })}
      >
        {/* Back Button (to Panel #1) */}
        <button className="flex gap-8" onClick={handleBackToSubMenu}>
          <ChevronIcon className="rotate-90" />
          <Typography
            color={Color.Charcoal}
            font="grotesk"
            weight="medium"
            size="body-md-fixed"
            text="Back"
            className="mb-32 hidden pt-[2px] sm:block"
          />
        </button>
        <div className="pl-16 sm:pl-32">
          {/* Render that section's menuContent blocks */}
          {activeSubSection !== undefined && activeSubMenu !== undefined && (
            <div className="flex flex-col gap-32 overflow-y-auto px-4">
              {mainLinks[activeSubMenu].subMenu?.content[
                activeSubSection!
              ].menuContent.map((contentBlock, cIdx) => (
                <SubMenuBlock
                  key={`mobile-sub-block-${cIdx}`}
                  {...contentBlock}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MobileNavPanels
